import React from 'react';
import styled from 'styled-components';
import { useNavigationBlock } from 'contexts/navigationBlock';
import { AppRoutes } from 'routes';
import { isKiosk, isMobile } from 'utils/platform';
import Button from 'ui/Button/Button';
import ArrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import ArrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import { useHistory } from 'react-router-dom';
import logo from './icons/logo.svg';
import { ButtonTypeEnum } from 'ui/Button/Button';
import media from 'ui/media';
import { useIntl } from 'react-intl';
import {
  darkScene,
  shouldShowDarkSceneWhenGoingBack,
  shouldShowMeasurementsWhenGoingBack,
  showMeasurements,
  showMeasurementsWhenGoingBack
} from 'ducks/layout';
import { useAppDispatch } from 'ducks/hooks';
import { useSelector } from 'react-redux';
import { isHelpLinesActive, setHelpLinesLabelsVisible, setHelpLinesVisible } from 'ducks/helpLines';
import useIsRtl from 'hooks/useIsRtl';
import { useCore } from 'contexts/core';
import { NightLightsSystem } from '@inter-ikea-storageone/range';
import { getKioskIntegration } from 'utils/integration';
import { useServiceSettings } from 'contexts/serviceSettings';

const headerHeight = isMobile() ? '3.75rem' : '6rem';

const StyledHeader = styled.header`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: #fff;
  flex-shrink: 0;
  height: ${headerHeight};
  align-items: center;
  margin: auto;
  width: 100%;
  max-width: 100rem;
  ${media.mobile`
    transition: top 0.4s ease-out;
    &[data-visible='false'] {
      top: -3.75rem
    }
  `}
`;

const LogoButton = styled.button`
  display: flex;
  all: unset;
  height: 36px;
  cursor: ${isKiosk() ? 'default' : 'pointer'};
`;

const HeaderContent = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  height: 100%;
`;

interface HeaderProps {
  children?: React.ReactNode;
  showLogo?: boolean;
  showBackButton?: boolean;
  backButtonTarget?: string;
  onBackClickedOverride?: () => void;
  visible?: boolean;
}

type IkeaLogoProps = {
  onClick: () => void;
};

export const IkeaLogo = ({ onClick, ...props }: IkeaLogoProps) => {
  const { formatMessage } = useIntl();
  return (
    <LogoButton onClick={onClick} {...props}>
      <img height="36" src={logo} alt={formatMessage({ id: 'storageone.ikeaHomeButton' })} />
    </LogoButton>
  );
};

const Header = ({
  children = null,
  showLogo,
  showBackButton,
  backButtonTarget,
  onBackClickedOverride,
  visible
}: HeaderProps) => {
  const history = useHistory();
  const { handleBlockedNavigation } = useNavigationBlock();
  const { formatMessage } = useIntl();
  const isRtl = useIsRtl();
  const kioskIntegration = getKioskIntegration();
  const coreApi = useCore();

  const {
    dexfSettings: {
      gallery: { experienceGalleryBackURL }
    }
  } = useServiceSettings();

  const dispatch = useAppDispatch();
  const showMeasurementLinesWhenNavigatingBackToPlanner = useSelector(
    shouldShowMeasurementsWhenGoingBack
  );
  const helpLinesSystemIsActive = useSelector(isHelpLinesActive);
  const showDarkSceneWhenNavigatingBackToPlanner = useSelector(shouldShowDarkSceneWhenGoingBack);

  const onGoToIkeaHome = () => {
    if (!isKiosk()) {
      handleBlockedNavigation({ pathname: AppRoutes.IKEA_HOME });
    }
  };

  const onBackClicked = () => {
    if (!backButtonTarget) return;
    if (backButtonTarget === AppRoutes.PLANNER) {
      // Always re-enable help lines if system is active
      if (helpLinesSystemIsActive) {
        dispatch(setHelpLinesVisible(true));

        if (showMeasurementLinesWhenNavigatingBackToPlanner) {
          dispatch(setHelpLinesLabelsVisible(true));
        }
      }

      // if measurement lines was active before going to summary we activate them again
      if (showMeasurementLinesWhenNavigatingBackToPlanner) {
        dispatch(showMeasurements());
        dispatch(showMeasurementsWhenGoingBack(false));
      }

      if (showDarkSceneWhenNavigatingBackToPlanner) {
        dispatch(darkScene());
        coreApi.getSystem<NightLightsSystem>('NightLightsSystem')?.enable();
      }
    }

    // if leaving planner to gallery then we must call exitClicked instead
    if (backButtonTarget === experienceGalleryBackURL) {
      kioskIntegration.exitClicked();
      return;
    }

    if (backButtonTarget.startsWith('/')) {
      history.push(backButtonTarget);
    } else if (backButtonTarget.startsWith('http')) {
      window.location.replace(backButtonTarget);
    }

    kioskIntegration.backClicked();
  };

  return (
    <StyledHeader data-visible={visible} data-testid="header">
      {showLogo && <IkeaLogo onClick={onGoToIkeaHome} />}
      {showBackButton && (
        <Button
          data-testid="header-back-button"
          type={ButtonTypeEnum.tertiary}
          onClick={onBackClickedOverride || onBackClicked}
          ssrIcon={isRtl ? ArrowRightIcon : ArrowLeftIcon}
          ml={showLogo ? 'mdl' : ''}
          aria-label={formatMessage({ id: 'storageone.back' })}
          iconOnly
        />
      )}
      <HeaderContent>{children}</HeaderContent>
    </StyledHeader>
  );
};

export default Header;
